import type {ReactElement} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import {addLocalePrefix} from '@/utilities/add-locale-prefix'
import type {Block} from '@/services/kontent-ai/types'
import {
	ReusableCTAButtonComponent,
	type CtaButtonContentItem,
} from '@/products/flexible-web-toolkit/blocks/cta-button'
import {KontentResponsivePicture} from '@/products/flexible-web-toolkit/components/responsive-picture'
import {useAuth} from '@/products/auth/auth-provider'
import {HeaderBlockWithCarousel} from './header-block-carousel'
import {HeaderBlockCarouselSlide} from './header-block-carousel-slide'

type HeaderBlockCarouselSlideContentItem = IContentItem<{
	heading: Elements.TextElement
	subheading: Elements.TextElement
	headingUrl: Elements.TextElement
	imgUrl: Elements.TextElement
	image: Elements.AssetsElement
	button: Elements.LinkedItemsElement<CtaButtonContentItem>
	isContentOnTopOfImage: Elements.MultipleChoiceElement
	mobileImage: Elements.AssetsElement
}>

export type HeaderBlockCarouselContentItem = IContentItem<{
	headerBlockCarouselSlides: Elements.LinkedItemsElement<HeaderBlockCarouselSlideContentItem>
	autoScroll: Elements.MultipleChoiceElement
	autoScrollDuration: Elements.NumberElement
}>

export const HeaderBlockCarouselBlock: Block<
	HeaderBlockCarouselContentItem
> = ({block, ...context}) => {
	const {locale} = useRouter()
	const authState = useAuth()

	const autoScrollEnabled: boolean =
		block.elements.autoScroll[0]?.codename === 'yes'
	let autoScrollDuration
	if (autoScrollEnabled) {
		if (
			block.elements.autoScrollDuration &&
			block.elements.autoScrollDuration > 0
		) {
			autoScrollDuration = block.elements.autoScrollDuration * 1000
		} else {
			autoScrollDuration = 3000
		}
	}

	return (
		<HeaderBlockWithCarousel
			autoScrollDuration={autoScrollDuration}
			autoScrollEnabled={autoScrollEnabled}
		>
			{block.elements.headerBlockCarouselSlides.map((slide, index) => {
				const isContentOnTopOfImage =
					slide.elements.isContentOnTopOfImage[0]?.codename === 'yes'
				const button = slide.elements.button[0]
				const mobileImage = slide.elements.mobileImage[0]
				const desktopImage = slide.elements.image[0] ?? mobileImage

				let imageElement: ReactElement<{
					className: string
				}> | null = null
				if (desktopImage) {
					imageElement = (
						<KontentResponsivePicture
							className="w-full object-cover"
							desktopAsset={desktopImage}
							fallbackHeight={552}
							fallbackWidth={1988}
							fetchPriority={index === 0 ? 'high' : 'auto'}
							mobileAsset={mobileImage}
							priority={index === 0}
							quality={65}
							sizes="100vw"
						/>
					)
				}
				if (authState.isBlockVisible(slide)) {
					return (
						<HeaderBlockCarouselSlide
							button={
								button ? (
									<ReusableCTAButtonComponent
										block={button}
										className={
											isContentOnTopOfImage
												? 'w-max lg:w-2/3'
												: undefined
										}
										key={button.system.id}
										{...context}
									/>
								) : null
							}
							heading={slide.elements.heading}
							headingUrl={addLocalePrefix(
								slide.elements.headingUrl || '/',
								locale ?? ''
							)}
							image={imageElement}
							isContentOnTopOfImage={isContentOnTopOfImage}
							key={slide.system.id}
							subheading={slide.elements.subheading}
							url={addLocalePrefix(
								slide.elements.imgUrl || '/',
								locale ?? ''
							)}
						/>
					)
				}
				return null
			})}
		</HeaderBlockWithCarousel>
	)
}
